import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { formatError } from "../../lib/errors";

interface EnrichmentPanelProps {
  entityType: "person" | "organization";
  entityId: string;
  enrichmentData: Record<string, unknown> | null | undefined;
  enrichedAt: string | null | undefined;
  onRefresh: () => void;
}

// A small, neutral panel showing Apollo enrichment status and a trigger
// button. Renders a compact summary when data exists and a full JSON dump
// behind an expand toggle.
export function EnrichmentPanel({
  entityType,
  entityId,
  enrichmentData,
  enrichedAt,
  onRefresh,
}: EnrichmentPanelProps) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "err" | "none"; text: string } | null>(
    null,
  );

  const remainingQ = useQuery({
    queryKey: ["enrichment_remaining_today"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("manna_enrichment_remaining_today");
      if (error) throw error;
      return data as number;
    },
    staleTime: 30_000,
  });

  const enrich = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("manna-enrich", {
        body: { entity_type: entityType, entity_id: entityId },
      });
      if (error) throw error;
      return data as { ok: true; matched: boolean; data?: Record<string, unknown> };
    },
    onSuccess: (res) => {
      if (res.matched) {
        setMessage({ kind: "ok", text: "Enrichment complete." });
        onRefresh();
      } else {
        setMessage({ kind: "none", text: "No match found in Apollo." });
      }
      qc.invalidateQueries({ queryKey: ["enrichment_remaining_today"] });
    },
    onError: (e) => setMessage({ kind: "err", text: formatError(e) }),
  });

  const hasData = enrichmentData && Object.keys(enrichmentData).length > 0;
  const remaining = remainingQ.data;

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<AutoAwesomeIcon fontSize="small" color="primary" />}
        title="Data enrichment"
        titleTypographyProps={{ variant: "h3" }}
        subheader={
          enrichedAt ? `Last enriched ${new Date(enrichedAt).toLocaleString()}` : "Not yet enriched"
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {typeof remaining === "number" && (
              <Chip
                label={`${remaining}/100 left today`}
                size="small"
                color={remaining > 0 ? "default" : "warning"}
              />
            )}
            <Button
              size="small"
              variant="contained"
              startIcon={enrich.isPending ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
              disabled={enrich.isPending || (typeof remaining === "number" && remaining <= 0)}
              onClick={() => {
                setMessage(null);
                enrich.mutate();
              }}
            >
              {enrich.isPending ? "Enriching…" : hasData ? "Re-enrich" : "Enrich"}
            </Button>
          </Stack>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {message && (
          <Alert
            severity={message.kind === "ok" ? "success" : message.kind === "err" ? "error" : "info"}
            sx={{ mb: 2 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        {hasData ? (
          <Box>
            <EnrichmentSummary entityType={entityType} data={enrichmentData} />
            <Button
              size="small"
              onClick={() => setExpanded((v) => !v)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ mt: 1 }}
            >
              {expanded ? "Hide raw data" : "Show raw data"}
            </Button>
            <Collapse in={expanded}>
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  fontSize: 12,
                  overflow: "auto",
                  maxHeight: 400,
                }}
              >
                {JSON.stringify(enrichmentData, null, 2)}
              </Box>
            </Collapse>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Click Enrich to fetch up-to-date company or contact data from Apollo.
          </Typography>
        )}

        <IconButton sx={{ display: "none" }} />
      </CardContent>
    </Card>
  );
}

// Compact summary pulls a handful of common fields out of the Apollo payload.
// Apollo schema varies; we defensively typecheck each field.
function EnrichmentSummary({
  entityType,
  data,
}: {
  entityType: "person" | "organization";
  data: Record<string, unknown>;
}) {
  if (entityType === "person") {
    return (
      <Stack spacing={0.5}>
        <KV k="Title" v={str(data.title)} />
        <KV k="Seniority" v={str(data.seniority)} />
        <KV k="Company" v={str((data.organization as Record<string, unknown> | undefined)?.name)} />
        <KV k="Location" v={joinCity(data)} />
        <KV k="LinkedIn" v={linkField(data.linkedin_url)} />
      </Stack>
    );
  }
  return (
    <Stack spacing={0.5}>
      <KV k="Industry" v={str(data.industry)} />
      <KV k="Employees" v={num(data.estimated_num_employees)} />
      <KV k="Revenue" v={num(data.annual_revenue)} />
      <KV k="Founded" v={num(data.founded_year)} />
      <KV k="Website" v={linkField(data.website_url)} />
      <KV k="Keywords" v={arrStr(data.keywords, 8)} />
    </Stack>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="caption" sx={{ width: 100, color: "text.secondary" }}>
        {k}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
        {v ?? <em style={{ color: "#999" }}>—</em>}
      </Typography>
    </Stack>
  );
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}
function num(v: unknown): string | null {
  return typeof v === "number" ? v.toLocaleString() : null;
}
function arrStr(v: unknown, max: number): string | null {
  if (!Array.isArray(v)) return null;
  const items = v.filter((x): x is string => typeof x === "string").slice(0, max);
  return items.length ? items.join(", ") : null;
}
function linkField(v: unknown): React.ReactNode | null {
  const s = str(v);
  if (!s) return null;
  return (
    <a href={s} target="_blank" rel="noopener noreferrer">
      {s.replace(/^https?:\/\//, "")}
    </a>
  );
}
function joinCity(d: Record<string, unknown>): string | null {
  const city = str(d.city);
  const state = str(d.state);
  const country = str(d.country);
  const parts = [city, state, country].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}
